import {
  useCallback, useContext, useEffect, useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  setPerfiladorInputValue,
  setPerfiladorResultado,
  setPerfiladorEnganche,
  setPerfiladorPlazo,
  setPerfiladorInitialDataValues,
  setPerfiladorInitialValues,
  setPerfiladorInputValueAndCalcEnganche,
  setConditionsByInmobiliariaId,
  setPerfiladorPercentageEnganche,
  setPerfiladorPercentageEngancheRecursosPropios,
  setPerfiladorMontoCredito,
  setPerfiladorMontoSubcuenta,
  setPerfiladorMontoCreditoInfonavit,
  setPerfiladorEngancheRecursosPropios,
  setPerfiladorEngancheRangos,
  syncMontoCreditoRangos,
} from '../store/perfilador/perfiladorSlice';
import {
  selectPerfiladorEnganche,
  selectPerfiladorEngancheNumber,
  selectPerfiladorEngancheRecursosPropios,
  selectPerfiladorEngancheRecursosPropiosNumber,
  selectPerfiladorFormattedResultado,
  selectPerfiladorGastosEscrituraPercentage,
  selectPerfiladorInputNumber,
  selectPerfiladorInputValue,
  selectPerfiladorengancheMinimoPercentage,
  selectPerfiladorPlazo,
  selectPerfiladorPlazoNumber,
  selectPerfiladorTasaNumber,
  selectPerfiladorPercentageEnganche,
  selectPerfiladorPercentageEngancheRecursosPropios,
  selectPerfiladorMontoCredito,
  selectPerfiladorIsMontoCreditoManual,
  selectPerfiladorSaldoSubcuentaInfonavit,
  selectPerfiladorMontoCreditoInfonavit,
  selectPerfiladorEngancheMinimoRecursosPropiosPercentage,
  selectMontoTotalInfonavit,
  selectPerfiladorEngancheRangos,
  selectPerfiladorMontoCreditoRangos,
} from '../store/perfilador';
import {
  getPerfiladorClientIngresos,
  getPerfiladorClientValuePropNumber,
} from '../storage';
import {
  convertirAMoneda,
  convertirANumero,
  getIngresosRecomendados,
  getViviendaEnganche,
  redondearNumero,
} from '../utils/currency';
import { getMonthlyPaid, storeIngresosData, storePerfiladorValuePropiety } from '../utils';
import {
  calcAmmountCreditoInfonavitBySubcuentaInfonavit,
  calcAmmountEnganche,
  calcAmmountGastosEscritura,
  calcAmmountPercentage,
} from '../utils/simulator.utils';

import {
  CALC_MODE_INGREOS_MENSUALES,
  CALC_MODE_PRECIO_INMUEBLE,
  CREDITO_HIPOTECARIO_INT_VALUES,
  DEFAULT_DECIMALES_REDONDEO,
  DEFAULT_PORCENTAJE_ENDEUDAMIENTO,
  DEFAULT_RPI,
  PERCENTAGE_ENGANCH_MULTIPLE,
} from '../constants';
import { PerfiladorContext } from '../context/perfilador';
import {
  fillValuesOnInitialValuesFromCreditObject,
  getCredit, getCreditObjectUnformatted,
  getCreditValuesObject, getLocalCreditValuesForPerfilador,
  storeCredit,
} from '../utils/credit';

export const usePerfilador = ({
  calcMode,
  initialData = {},
  useDesembolsoMode = false,
  autoCalcEnganche = true,
  includeDecimals = true,
  autoFetchInitialValues = false,
}) => {
  const dispatch = useDispatch();
  const { currentInmobiliariaId } = useContext(PerfiladorContext);
  const [initialEngancheNumber, setInitialEngancheNumber] = useState(0);

  const inputValue = useSelector(selectPerfiladorInputValue);
  const inputNumber = useSelector(selectPerfiladorInputNumber);
  const plazo = useSelector(selectPerfiladorPlazo);
  const plazoNumber = useSelector(selectPerfiladorPlazoNumber);
  const enganche = useSelector(selectPerfiladorEnganche); // Ahora es el enganche total
  const engancheNumber = useSelector(selectPerfiladorEngancheNumber); // Enganche total number
  const engancheRecursosPropios = useSelector(selectPerfiladorEngancheRecursosPropios);
  const engancheRecursosPropiosNumber = useSelector(
    selectPerfiladorEngancheRecursosPropiosNumber,
  );
  const montoCreditoValue = useSelector(selectPerfiladorMontoCredito);
  const isMontoCreditoManual = useSelector(selectPerfiladorIsMontoCreditoManual);
  const percentageEnganche = useSelector(selectPerfiladorPercentageEnganche); // % enganche total
  const percentageEngancheRecursosPropios = useSelector(
    selectPerfiladorPercentageEngancheRecursosPropios,
  ); // % recursos propios
  const resultado = useSelector(selectPerfiladorFormattedResultado);
  const tasaNumber = useSelector(selectPerfiladorTasaNumber);
  const engancheMinimoPercentage = useSelector(selectPerfiladorengancheMinimoPercentage);
  const engancheMinimoRecursosPropiosPercentage = useSelector(
    selectPerfiladorEngancheMinimoRecursosPropiosPercentage,
  );
  const gastosEscrituraPercentage = useSelector(selectPerfiladorGastosEscrituraPercentage);
  const saldoSubcuentaInfonavit = useSelector(selectPerfiladorSaldoSubcuentaInfonavit);
  const montoCreditoInfonavit = useSelector(selectPerfiladorMontoCreditoInfonavit);
  const montoTotalInfonavit = useSelector(selectMontoTotalInfonavit);
  const engancheRangos = useSelector(selectPerfiladorEngancheRangos);
  const montoCreditoRangos = useSelector(selectPerfiladorMontoCreditoRangos);

  // Helper para obtener el monto total de Infonavit (subcuenta + crédito)
  const calculoMensualidad = useCallback((importe) => {
    const tasaAnual = tasaNumber * 0.01;
    const plazoMensual = plazoNumber * 12;
    const result = getMonthlyPaid({ importe, plazoMensual, tasa: tasaAnual });
    return Number(result.toFixed(2));
  }, [plazoNumber, tasaNumber]);

  const calculoMontoCredito = useCallback((valor, engancheTotalValue) => {
    const importe = valor - engancheTotalValue;
    return Number(importe.toFixed(2));
  }, []);

  const calculoIngresosRecomendados = useCallback((pagoMensual) => {
    const result = getIngresosRecomendados({
      pagoMensual,
      porcentajeEndeudamiento: DEFAULT_PORCENTAJE_ENDEUDAMIENTO,
    });
    return Number(result.toFixed(2));
  }, []);

  const calcularPrecioInmueble = useCallback(() => {
    // engancheNumber ahora es el enganche total
    const montoCredito = isMontoCreditoManual && montoCreditoValue
      ? montoCreditoValue
      : calculoMontoCredito(inputNumber, engancheNumber);
    const pagoMensual = calculoMensualidad(montoCredito);
    const ingresosRecomendados = calculoIngresosRecomendados(pagoMensual);
    const montoGastosEscritura = useDesembolsoMode
      ? calcAmmountGastosEscritura(inputNumber, gastosEscrituraPercentage)
      : 0;

    return {
      propietyValue: redondearNumero(inputNumber, DEFAULT_DECIMALES_REDONDEO),
      montoCredito: redondearNumero(montoCredito, DEFAULT_DECIMALES_REDONDEO),
      pagoMensual: redondearNumero(pagoMensual, DEFAULT_DECIMALES_REDONDEO),
      ingresosRecomendados: redondearNumero(ingresosRecomendados, DEFAULT_DECIMALES_REDONDEO),
      montoGastosEscritura: redondearNumero(montoGastosEscritura, DEFAULT_DECIMALES_REDONDEO),
    };
  }, [
    inputNumber,
    engancheNumber,
    gastosEscrituraPercentage,
    tasaNumber,
    plazoNumber,
    montoCreditoValue,
    isMontoCreditoManual,
    calculoMontoCredito,
    calculoMensualidad,
    calculoIngresosRecomendados,
    useDesembolsoMode,
  ]);

  const calcularIngresosMensuales = useCallback(() => {
    const { valorVivienda } = getViviendaEnganche({
      ingresos: inputNumber,
      tasa: tasaNumber * 0.01,
      RPI: DEFAULT_RPI,
    });

    const propietyValue = Number(valorVivienda.toFixed(2));
    const { enganche: _enganche, engancheNumber: _engancheNumber } = calcAmmountEnganche(
      propietyValue,
      includeDecimals,
      engancheMinimoPercentage,
    );

    // Calcular enganche total considerando montoTotalInfonavit
    const _engancheTotalNumber = _engancheNumber + montoTotalInfonavit;

    const montoCredito = montoCreditoValue
      || calculoMontoCredito(propietyValue, _engancheTotalNumber);
    const pagoMensual = calculoMensualidad(montoCredito);
    const montoGastosEscritura = useDesembolsoMode
      ? calcAmmountGastosEscritura(propietyValue, gastosEscrituraPercentage)
      : 0;

    dispatch(setPerfiladorEnganche(_enganche));

    return {
      propietyValue: redondearNumero(propietyValue, DEFAULT_DECIMALES_REDONDEO),
      montoCredito: redondearNumero(montoCredito, DEFAULT_DECIMALES_REDONDEO),
      pagoMensual: redondearNumero(pagoMensual, DEFAULT_DECIMALES_REDONDEO),
      montoGastosEscritura: redondearNumero(
        montoGastosEscritura,
        DEFAULT_DECIMALES_REDONDEO,
      ),
    };
  }, [
    inputNumber,
    montoCreditoValue,
    engancheMinimoPercentage,
    gastosEscrituraPercentage,
    montoTotalInfonavit,
    calculoMontoCredito,
    calculoMensualidad,
    useDesembolsoMode,
  ]);

  const getAndSetIngresosCliente = useCallback(() => {
    const localIngresos = getPerfiladorClientIngresos();
    if (localIngresos) {
      const ingresosCurrency = convertirAMoneda(localIngresos);
      dispatch(setPerfiladorInputValue(ingresosCurrency));
    } else {
      dispatch(setPerfiladorInputValue(''));
    }
  }, [dispatch]);

  const getAndSetPropietyValue = useCallback(() => {
    const localClientPropNumber = getPerfiladorClientValuePropNumber();
    if (localClientPropNumber) {
      const value = convertirAMoneda(localClientPropNumber, includeDecimals);

      dispatch(
        setPerfiladorInputValueAndCalcEnganche({
          value,
          includeDecimals,
          engancheMinimoPercentage,
        }),
      );
    } else {
      dispatch(setPerfiladorInputValue(''));
    }
  }, [dispatch, includeDecimals, engancheMinimoPercentage]);

  const storageCurrentCreditData = () => {
    const {
      montoMensual = 0,
      sueldoMensualBruto = 0,
      montoCredito = 0,
      pagoMensual = 0,
      montoGastosEscritura = 0,
    } = resultado;
    const storageData = {
      montoMensual,
      sueldoMensualBruto,
      importe: montoCredito,
      meses: plazoNumber * 12,
      aforo: (montoCredito / inputNumber).toFixed(4),
      enganche: engancheNumber, // Ahora es el enganche total
      engancheRecursosPropios: engancheRecursosPropiosNumber,
      year: plazoNumber,
      plazo,
      mensualidad: pagoMensual,
      propietyValue: inputNumber,
      montoGastosEscritura,
      percentageEnganche,
      saldoSubcuentaInfonavit,
      montoCreditoInfonavit,
    };
    const oldCreditData = getCredit();
    storeCredit({
      ...oldCreditData,
      ...storageData,
    });
  };

  const handleChangeValuePropiety = useCallback((value) => {
    dispatch(
      setPerfiladorInputValueAndCalcEnganche({
        value,
        includeDecimals,
        engancheMinimoPercentage,
      }),
    );
    storePerfiladorValuePropiety(value);
  }, [dispatch, engancheMinimoPercentage, includeDecimals]);

  const handleChangeIngresosValue = useCallback((value) => {
    dispatch(setPerfiladorInputValue(value));
    storeIngresosData(value);
  }, [dispatch]);

  const actionsByCalcMode = useMemo(() => {
    switch (calcMode) {
      case CALC_MODE_INGREOS_MENSUALES:
        return {
          onChange: handleChangeIngresosValue,
          getAndSet: getAndSetIngresosCliente,
          calculate: calcularIngresosMensuales,
        };
      case CALC_MODE_PRECIO_INMUEBLE:
      default:
        return {
          onChange: handleChangeValuePropiety,
          getAndSet: getAndSetPropietyValue,
          calculate: calcularPrecioInmueble,
        };
    }
  }, [
    calcMode,
    inputValue,
    engancheMinimoPercentage,
    gastosEscrituraPercentage,
    plazoNumber,
    engancheNumber,
    montoCreditoValue,
    saldoSubcuentaInfonavit,
    isMontoCreditoManual,
    tasaNumber,
  ]);

  const onChange = useCallback(
    (event, value) => {
      actionsByCalcMode.onChange(value);
    },
    [dispatch, calcMode, autoCalcEnganche, engancheMinimoPercentage],
  );

  const fixPercentageEnganche = useCallback((percentage) => {
    // Si el enganche no es multiplo de 5, se debe redondear al multiplo de 5 mas cercano
    const newPercentage = Math.round(
      percentage / PERCENTAGE_ENGANCH_MULTIPLE,
    ) * PERCENTAGE_ENGANCH_MULTIPLE;
    return newPercentage;
  }, []);

  // Helper genérico para modificar porcentajes de enganche
  const adjustPercentage = useCallback((
    currentPercentage,
    increment,
    minPercentage,
    maxPercentage,
    setterAction,
  ) => {
    const newPercentage = currentPercentage + increment;
    const fixedPercentage = fixPercentageEnganche(newPercentage);

    if (fixedPercentage < minPercentage || fixedPercentage > maxPercentage) return;

    const newEngancheNumber = (inputNumber * fixedPercentage) / 100;
    const newEnganche = convertirAMoneda(newEngancheNumber, includeDecimals);

    dispatch(setterAction(newEnganche));
  }, [dispatch, fixPercentageEnganche, inputNumber, includeDecimals]);

  // Handlers para enganche total (general)
  const decreasePercentageEnganche = useCallback(() => {
    const newPercentageTotal = percentageEnganche - PERCENTAGE_ENGANCH_MULTIPLE;
    const fixedPercentageTotal = fixPercentageEnganche(newPercentageTotal);

    // El mínimo es el enganche mínimo requerido
    if (fixedPercentageTotal < engancheMinimoPercentage) return;

    // Calcular el enganche total deseado
    const newEngancheTotalNumber = (inputNumber * fixedPercentageTotal) / 100;

    // Calcular recursos propios (total - aporte Infonavit)
    const newRecursosPropiosNumber = Math.max(0, newEngancheTotalNumber - montoTotalInfonavit);
    const newRecursosPropios = convertirAMoneda(newRecursosPropiosNumber, includeDecimals);

    dispatch(setPerfiladorEngancheRecursosPropios(newRecursosPropios));
  }, [
    percentageEnganche,
    inputNumber,
    engancheMinimoPercentage,
    montoTotalInfonavit,
    fixPercentageEnganche,
    includeDecimals,
    dispatch,
  ]);

  const increasePercentageEnganche = useCallback(() => {
    const newPercentageTotal = percentageEnganche + PERCENTAGE_ENGANCH_MULTIPLE;
    const fixedPercentageTotal = fixPercentageEnganche(newPercentageTotal);

    if (fixedPercentageTotal >= 100) return;

    // Calcular el enganche total deseado
    const newEngancheTotalNumber = (inputNumber * fixedPercentageTotal) / 100;

    // Calcular recursos propios (total - aporte Infonavit)
    const newRecursosPropiosNumber = Math.max(0, newEngancheTotalNumber - montoTotalInfonavit);
    const newRecursosPropios = convertirAMoneda(newRecursosPropiosNumber, includeDecimals);

    dispatch(setPerfiladorEngancheRecursosPropios(newRecursosPropios));
  }, [
    percentageEnganche,
    inputNumber,
    montoTotalInfonavit,
    fixPercentageEnganche,
    includeDecimals,
    dispatch,
  ]);

  // Handlers para enganche recursos propios
  const decreasePercentageEngancheRecursosPropios = useCallback(() => {
    adjustPercentage(
      percentageEngancheRecursosPropios,
      -PERCENTAGE_ENGANCH_MULTIPLE,
      0, // Puede ser 0% si Infonavit cubre todo
      100,
      setPerfiladorEngancheRecursosPropios,
    );
  }, [percentageEngancheRecursosPropios, adjustPercentage]);

  const increasePercentageEngancheRecursosPropios = useCallback(() => {
    const recursosFaltantes = inputNumber - montoTotalInfonavit;
    const maxPercentage = (recursosFaltantes / inputNumber) * 100;
    adjustPercentage(
      percentageEngancheRecursosPropios,
      PERCENTAGE_ENGANCH_MULTIPLE,
      0,
      maxPercentage,
      setPerfiladorEngancheRecursosPropios,
    );
  }, [percentageEngancheRecursosPropios, adjustPercentage]);

  const addMontoToEnganche = useCallback((monto) => {
    const newEngancheNumber = initialEngancheNumber + convertirANumero(monto);
    const newEngancheValue = convertirAMoneda(newEngancheNumber);
    dispatch(setPerfiladorEnganche(newEngancheValue));
  }, [dispatch, engancheNumber]);

  const onChangeMontoSubcuentaInfonavit = useCallback((value) => {
    const newMontoCreditoInfonavit = calcAmmountCreditoInfonavitBySubcuentaInfonavit(value);
    dispatch(setPerfiladorMontoSubcuenta(value));
    dispatch(setPerfiladorMontoCreditoInfonavit(newMontoCreditoInfonavit));
  }, [dispatch]);

  const onChangePlazo = useCallback((event, value) => {
    dispatch(setPerfiladorPlazo(value));
  }, [dispatch]);

  const onChangeEnganche = useCallback((event, value) => {
    // Modifica los recursos propios del usuario
    dispatch(setPerfiladorEnganche(value));
  }, [dispatch]);

  const onChangeEngancheRecursosPropios = useCallback((event, value) => {
    dispatch(setPerfiladorEngancheRecursosPropios(value));
  }, [dispatch]);

  const onChangeMontoCredito = useCallback((event, value) => {
    dispatch(setPerfiladorMontoCredito(value));
  }, [dispatch]);

  /**
   * Establece los rangos de enganche de recursos propios
   * @param {Object} rangos - { minimo, maximo, restarInfonavit? }
   * @param {number} rangos.minimo - Monto mínimo de enganche
   * @param {number} rangos.maximo - Monto máximo de enganche
   * @param {boolean} rangos.restarInfonavit - Si true, resta montoTotalInfonavit de los rangos
   */
  const setEngancheRangos = useCallback((rangos) => {
    dispatch(setPerfiladorEngancheRangos(rangos));
  }, [dispatch]);

  const loadPerfiladorInitialData = useCallback((_initialData) => {
    dispatch(setPerfiladorInitialDataValues(_initialData));
    const {
      precio = {},
      enganche: _enganche = {},
      year = {},
      engancheRecursosPropios: _engancheRecursosPropios = {},
      saldoSubcuentaInfonavit: _saldoSubcuentaInfonavit = {},
      montoCreditoInfonavit: _montoCreditoInfonavit = {},
    } = _initialData;
    const precioV = precio.value || '';
    const engancheV = _enganche.value || '';
    const yearV = year.value || '';
    const engancheRecursosPropiosV = _engancheRecursosPropios.value || '';
    const saldoSubcuentaInfonavitV = _saldoSubcuentaInfonavit.value || '';
    const montoCreditoInfonavitV = _montoCreditoInfonavit.value || '';

    setInitialEngancheNumber(convertirANumero(engancheV));
    dispatch(setPerfiladorInitialValues({
      precio: precioV,
      enganche: engancheV,
      plazo: yearV,
      engancheRecursosPropios: engancheRecursosPropiosV,
      saldoSubcuentaInfonavit: saldoSubcuentaInfonavitV,
      montoCreditoInfonavit: montoCreditoInfonavitV,
    }));
  }, [dispatch]);

  const fetchAndSetInitialValues = useCallback(async () => {
    const defaultInputValuesData = CREDITO_HIPOTECARIO_INT_VALUES;
    const creditFormattedValues = await getLocalCreditValuesForPerfilador();
    const creditObjectUnformatted = getCreditObjectUnformatted(creditFormattedValues);
    const creditObject = getCreditValuesObject(creditObjectUnformatted);
    const newValues = fillValuesOnInitialValuesFromCreditObject(
      defaultInputValuesData,
      creditObject,
    );
    loadPerfiladorInitialData(newValues);
  }, [dispatch, loadPerfiladorInitialData]);

  useEffect(() => {
    actionsByCalcMode.getAndSet();
  }, [
    calcMode,
    engancheMinimoPercentage,
    gastosEscrituraPercentage,
    getAndSetIngresosCliente,
    getAndSetPropietyValue,
  ]);

  useEffect(() => {
    const resultadoCalculo = actionsByCalcMode.calculate();
    dispatch(setPerfiladorResultado(resultadoCalculo));
  }, [
    inputValue,
    plazoNumber,
    engancheNumber,
    montoCreditoValue,
    saldoSubcuentaInfonavit,
    isMontoCreditoManual,
    calcularPrecioInmueble,
    calcularIngresosMensuales,
    dispatch,
    actionsByCalcMode,
  ]);

  useEffect(() => {
    if (Object.keys(initialData).length === 0) return;
    loadPerfiladorInitialData(initialData);
  }, [initialData, dispatch]);

  useEffect(() => {
    if (!autoFetchInitialValues) return;
    fetchAndSetInitialValues();
  }, [autoFetchInitialValues, dispatch]);

  useEffect(() => {
    if (!currentInmobiliariaId) return;
    dispatch(setConditionsByInmobiliariaId({ inmobiliariaId: currentInmobiliariaId }));
  }, [currentInmobiliariaId]);

  // Calcular porcentaje del enganche total
  useEffect(() => {
    const newPercentageTotal = calcAmmountPercentage(inputNumber, engancheNumber);
    dispatch(setPerfiladorPercentageEnganche(newPercentageTotal));
  }, [inputNumber, engancheNumber, dispatch]);

  // Calcular porcentaje de recursos propios
  useEffect(() => {
    const newPercentageRecursosPropios = calcAmmountPercentage(
      inputNumber,
      engancheRecursosPropiosNumber,
    );
    dispatch(setPerfiladorPercentageEngancheRecursosPropios(newPercentageRecursosPropios));
  }, [inputNumber, engancheRecursosPropiosNumber, dispatch]);

  // Sincronizar rangos de monto de crédito al state para visibilidad en Redux DevTools
  useEffect(() => {
    dispatch(syncMontoCreditoRangos(montoCreditoRangos));
  }, [montoCreditoRangos, dispatch]);

  return {
    inputValue,
    inputNumber,
    enganche, // Enganche total (montoTotalInfonavit + recursos propios)
    engancheNumber, // Enganche total number
    engancheRecursosPropios,
    engancheRecursosPropiosNumber,
    plazo,
    plazoNumber,
    percentageEnganche, // Porcentaje del enganche total
    percentageEngancheRecursosPropios, // Porcentaje de recursos propios
    montoCreditoValue,
    engancheMinimoPercentage,
    engancheMinimoRecursosPropiosPercentage,
    saldoSubcuentaInfonavit,
    montoCreditoInfonavit,
    montoTotalInfonavit,
    // Rangos
    engancheRangos, // Rangos de enganche de recursos propios { minimo, maximo }
    montoCreditoRangos, // Rangos de monto de crédito calculados { minimo, maximo }
    // Handlers
    addMontoToEnganche,
    onChange,
    onChangePlazo,
    onChangeEnganche, // Modifica recursos propios
    onChangeEngancheRecursosPropios, // Alias explícito para modificar recursos propios
    onChangeMontoCredito,
    onChangeMontoSubcuentaInfonavit,
    setEngancheRangos, // Para establecer rangos de enganche
    storageCurrentCreditData,
    loadPerfiladorInitialData, // Para cargar datos iniciales desde fuera del hook
    // Handlers para enganche total
    decreasePercentageEnganche,
    increasePercentageEnganche,
    // Handlers para recursos propios
    decreasePercentageEngancheRecursosPropios,
    increasePercentageEngancheRecursosPropios,
    ...resultado,
  };
};
